import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PaperPlaneTilt, 
  Camera, 
  CheckCircle, 
  Spinner,
  ArrowLeft,
  ChatCircleDots,
  X,
  Image as ImageIcon
} from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import InteractiveHouse from '@/components/InteractiveHouse';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Message bubble component
const MessageBubble = ({ message, isUser }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.3 }}
    className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
  >
    <div 
      className={`
        max-w-[85%] sm:max-w-[70%] px-5 py-3.5 rounded-3xl
        ${isUser 
          ? 'bg-amber-500 text-slate-900 rounded-br-lg' 
          : 'bg-white dark:bg-zinc-800 text-foreground rounded-bl-lg shadow-lg shadow-black/5'
        }
      `}
    >
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
      <p className={`text-[10px] mt-2 ${isUser ? 'text-slate-700' : 'text-muted-foreground'}`}>
        {new Date(message.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  </motion.div>
);

// Typing indicator
const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex justify-start mb-4"
  >
    <div className="bg-white dark:bg-zinc-800 px-5 py-4 rounded-3xl rounded-bl-lg shadow-lg shadow-black/5">
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </motion.div>
);

export default function ClientFormChat() {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationComplete, setConversationComplete] = useState(false);
  const [collectedData, setCollectedData] = useState(null);
  const [estimation, setEstimation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    startConversation();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startConversation = async () => {
    try {
      const response = await fetch(`${API}/public/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      setConversationId(data.conversation_id);
      setMessages([{
        id: Date.now(),
        content: data.message,
        isUser: false,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erreur de connexion');
    }
  };

  const sendMessage = async () => {
    if ((!inputValue.trim() && photos.length === 0) || !conversationId || isTyping) return;

    const userMessage = {
      id: Date.now(),
      content: inputValue || (photos.length > 0 ? `[${photos.length} photo(s) envoyée(s)]` : ''),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputValue;
    const photosToSend = [...photos];
    setInputValue('');
    setPhotos([]);
    setIsTyping(true);

    try {
      const response = await fetch(`${API}/public/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: messageText,
          photos: photosToSend
        })
      });
      
      const data = await response.json();
      
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        content: data.message,
        isUser: false,
        timestamp: new Date()
      }]);

      if (data.conversation_complete) {
        setConversationComplete(true);
        setCollectedData(data.final_data);
        setEstimation(data.estimation);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur d\'envoi');
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setPhotos(prev => [...prev, base64]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitRequest = async () => {
    if (!collectedData) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/public/chat/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: conversationId,
          collected_data: collectedData
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success('Demande envoyée !', { description: 'Nous vous recontactons rapidement.' });
      } else {
        toast.error(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-emerald-600" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Demande envoyée !</h1>
          <p className="text-muted-foreground mb-6">
            Merci pour votre demande. Notre équipe vous recontactera très rapidement avec un devis personnalisé.
          </p>
          {estimation && (
            <div className="angela-card mb-6">
              <p className="text-sm text-muted-foreground mb-1">Estimation indicative</p>
              <p className="text-2xl font-bold text-foreground">
                {estimation.price_min?.toLocaleString('fr-FR')} € - {estimation.price_max?.toLocaleString('fr-FR')} €
              </p>
              <p className="text-xs text-muted-foreground mt-1">HT • Devis détaillé à venir</p>
            </div>
          )}
          <button 
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold transition-colors"
            data-testid="new-request-btn"
          >
            <PaperPlaneTilt size={20} weight="duotone" />
            Nouvelle demande
          </button>
        </motion.div>
      </div>
    );
  }

  // Build description from all messages for the house
  const fullDescription = messages
    .filter(m => m.isUser)
    .map(m => m.content)
    .join(' ');

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row" data-testid="chat-container">
      <Toaster position="top-center" richColors />
      
      {/* Left Side - Chat (full on mobile, half on desktop) */}
      <div className="flex-1 flex flex-col lg:w-1/2 lg:border-r lg:border-border">
        {/* Header */}
        <header className="bg-card border-b border-border px-4 py-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
            <ChatCircleDots size={24} className="text-slate-900" weight="duotone" />
          </div>
          <div>
            <h1 className="font-bold text-foreground">Assistant Devis</h1>
            <p className="text-sm text-muted-foreground">Angela • Réponse instantanée</p>
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 bg-muted/30" data-testid="messages-container">
          <div className="max-w-2xl mx-auto lg:max-w-none">
            <AnimatePresence>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} isUser={message.isUser} />
              ))}
            </AnimatePresence>
            
            <AnimatePresence>
              {isTyping && <TypingIndicator />}
            </AnimatePresence>
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Photo Preview */}
        {photos.length > 0 && (
          <div className="bg-card border-t border-border px-4 py-3">
            <div className="max-w-2xl mx-auto lg:max-w-none flex gap-2 overflow-x-auto no-scrollbar">
              {photos.map((photo, index) => (
                <div key={index} className="relative flex-shrink-0">
                  <img 
                    src={`data:image/jpeg;base64,${photo}`}
                    alt={`Photo ${index + 1}`}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  <button
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg"
                    data-testid={`remove-photo-${index}`}
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conversation Complete */}
        {conversationComplete && !submitted && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800 px-4 py-5"
            data-testid="chat-complete-section"
          >
            <div className="max-w-2xl mx-auto lg:max-w-none">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle size={24} className="text-emerald-600" weight="duotone" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="chat-complete-message">
                    Informations complètes !
                  </p>
                  <p className="text-sm text-emerald-600/80 dark:text-emerald-500">
                    Votre demande est prête à être envoyée
                  </p>
                </div>
              </div>
              
              {estimation && (
                <div className="angela-card mb-4 !p-4" data-testid="chat-estimation-card">
                  <p className="text-sm text-muted-foreground mb-1">Estimation indicative</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="chat-estimation-price">
                    {estimation.price_min?.toLocaleString('fr-FR')} € - {estimation.price_max?.toLocaleString('fr-FR')} € HT
                  </p>
                </div>
              )}
              
              <button 
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="chat-submit-button"
              >
                {submitting ? (
                  <Spinner size={20} className="animate-spin" />
                ) : (
                  <>
                    <PaperPlaneTilt size={20} weight="bold" />
                    Envoyer ma demande de devis
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Input Area */}
        {!conversationComplete && (
          <div className="bg-card border-t border-border px-4 py-4 pb-safe" data-testid="chat-input-area">
            <div className="max-w-2xl mx-auto lg:max-w-none flex gap-3 items-end">
              {/* Photo button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                className="hidden"
                data-testid="chat-photo-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                data-testid="chat-photo-button"
              >
                <Camera size={24} className="text-muted-foreground" />
              </button>
              
              {/* Text input */}
              <div className="flex-1 relative">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Décrivez votre projet..."
                  className="h-12 rounded-xl border-border pr-4 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  disabled={isTyping}
                  data-testid="chat-message-input"
                />
              </div>
              
              {/* Send button */}
              <button
                onClick={sendMessage}
                disabled={isTyping || (!inputValue.trim() && photos.length === 0)}
                className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500 hover:bg-amber-600 flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                data-testid="chat-send-button"
              >
                {isTyping ? (
                  <Spinner size={22} className="text-slate-900 animate-spin" />
                ) : (
                  <PaperPlaneTilt size={22} className="text-slate-900" weight="bold" />
                )}
              </button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center mt-3">
              Appuyez sur Entrée pour envoyer • Ajoutez des photos si nécessaire
            </p>
          </div>
        )}
      </div>

      {/* Right Side - Interactive House (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <InteractiveHouse description={fullDescription} />
      </div>
    </div>
  );
}

